import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Location } from '../../../clients/quipli-service/location/location';
import {
  Order,
  OrderDocumentType,
  OrderReturnStatus,
  OrderSearchRequest,
  OrderStatus,
  OrderType,
} from '../../../clients/quipli-service/order/order';
import { DateTime, Interval } from 'luxon';
import { TabNavV2 } from '../../../components/tab-nav-v2/tab-nav-v2';
import { DataTable, DataTableColumn, DataTableRow } from '../../../components/table/data-table';
import {
  ActionCell,
  AmountCell,
  AssignedUnitCell,
  CancelledReasonCell,
  CustomerNameCell,
  DateTimeCell,
  DeliveryCell,
  LocationCell,
  OrderNumberCell,
  OrderStatusCell,
  PastDueCell,
  ProductNameCell,
  RentalEndCell,
  RentalStartCell,
  RowNumCell,
} from '../../order/order-table-cells';
import { Button } from '../../../components/form/button';
import { Typography } from '../../../components/typography/typography';
import Icon from '../../../components/icons/icon';
import { exportToCSV } from '../../order/order-utils';
import { Configuration } from '../../../clients/quipli-service/configuration/configuration';
import { Spinner } from '../../../components/spinner/spinner';
import { debounce } from 'lodash';
import useStickyFilters from '../../../hooks/use-sticky-filters';
import useRememberQueryParams from '../../../hooks/use-remember-query-params';
import { toast } from '../../../util/toasts';

const LocationContext = createContext<Location[]>([]);

const tabNames = ["Today's Orders", "Tomorrow's Orders", 'Past Due', 'Latest 5 Orders', 'Missed Rentals'];

export const RevenueOrdersTable = () => {
  const { listLocations, getMultipleConfigurations } = useAPI();
  const [locations, setLocations] = useState<Location[]>([]);
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [keyword, setKeyword] = useState<string>('');
  const [selectedTab, setSelectedTab] = useState<string>('');

  const setKeywordDelayed = useCallback(
    debounce((keyword: string) => setKeyword(keyword), 500),
    [],
  );

  useEffect(() => {
    listLocations()
      .then(setLocations)
      .catch(() => setLocations([]));
    getMultipleConfigurations(['CONF_UNIT_TRACKING_VIA', 'CONF_ALLOW_MULTI_LOCATION_UNITS'])
      .then((response) => {
        setConfigurations(response);
      })
      .catch(() => setConfigurations([]));
  }, []);

  const getTabContent = (tab: string, configurations: Configuration[], keyword: string): ReactNode => {
    const confUnitTrackingVia =
      configurations.find((conf) => conf.variable === 'CONF_UNIT_TRACKING_VIA')?.value === '1'
        ? 'serialNumber'
        : 'unitNumber';

    switch (tab) {
      case "Today's Orders":
        return <TodaysOrders trackingVia={confUnitTrackingVia} keyword={keyword} />;
      case "Tomorrow's Orders":
        return <TomorrowsOrders trackingVia={confUnitTrackingVia} keyword={keyword} />;
      case 'Past Due':
        return <PastDueOrders trackingVia={confUnitTrackingVia} keyword={keyword} />;
      case 'Latest 5 Orders':
        return <Latest5Orders trackingVia={confUnitTrackingVia} keyword={keyword} />;
      case 'Missed Rentals':
        return <CancelledOrders trackingVia={confUnitTrackingVia} keyword={keyword} />;
      default:
        return null;
    }
  };

  return (
    <LocationContext.Provider value={locations}>
      {configurations.length > 0 ? (
        <>
          <div className="bg-white w-full mb-[-20px] rounded-t-2xl">
            <input
              className="min-w-[300px] m-[16px] text-[14px]"
              placeholder="Search Orders"
              onChange={(e) => setKeywordDelayed(e.target.value)}
            />
          </div>
          <TabNavV2
            className="p-[16px]"
            tabNames={tabNames}
            onSelect={(tab) => {
              setSelectedTab(tab);
              return null;
            }}
          />
          {getTabContent(selectedTab, configurations, keyword)}
        </>
      ) : (
        <Spinner />
      )}
    </LocationContext.Provider>
  );
};

const DownloadButton = ({ downloadFunction = () => {} }) => (
  <div className="bg-white py-[16px] px-[25px] flex flex-end">
    <Button className="button-secondary" onClick={downloadFunction}>
      <Icon icon="Download" stroke="var(--grey-700)" />
      <Typography type="text-sm" weight="semi-bold" className="text-[var(--grey-700)]">
        {'Download'}
      </Typography>
    </Button>
  </div>
);

const TodaysOrders = ({ trackingVia, keyword }: { trackingVia: 'serialNumber' | 'unitNumber'; keyword: string }) => {
  const locations = useContext(LocationContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<DataTableRow[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const { searchOrders } = useAPI();

  const searchKey = 'dashboard_todays_orders';
  const params = useRememberQueryParams(searchKey);
  const { request, setRequest } = useStickyFilters<OrderSearchRequest>(
    searchKey,
    {
      sortBy: params?.has('sortBy') ? (params.get('sortBy')! as OrderSearchRequest['sortBy']) : 'id',
      sortDirection: params?.has('sortDirection')
        ? (params.get('sortDirection')! as OrderSearchRequest['sortDirection'])
        : 'desc',
    },
    () => {
      if (locations.length) {
        getOrders();
      }
    },
    [keyword, locations],
  );

  const columns: DataTableColumn[] = [
    { key: 'RowNum', label: '#' },
    { key: 'OrderNumber', label: 'Order No' },
    { key: 'CustomerName', label: 'Customer' },
    { key: 'RequestedTime', label: 'Requested Time' },
    { key: 'Location', label: 'Location' },
    { key: 'Delivery', label: 'Delivery' },
    { key: 'AssignedUnit', label: 'Assigned Unit(s)' },
    { key: 'RentalStart', label: 'Rental Start', sortable: true, sortField: 'rentalStart' },
    { key: 'RentalEnd', label: 'Rental End', sortable: true, sortField: 'rentalEnd' },
    { key: 'Status', label: 'Status' },
    { key: 'Amount', label: 'Amount' },
    { key: 'ActionColumn', label: 'Actions' },
  ];

  const buildDataRow = (order: Order) => {
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium">
            {order.user.name}
          </Typography>
          <Typography type="text-sm" weight="medium">
            <Typography type="text-sm" weight="regular" className="inline text-[var(--grey-500)]">
              Inv. NO{' '}
            </Typography>
            {order.orderNumber}
          </Typography>
        </>
      ),
      fields: {
        RowNum: <RowNumCell order={order} />,
        OrderNumber: <OrderNumberCell order={order} />,
        CustomerName: <CustomerNameCell order={order} />,
        RequestedTime: <DateTimeCell date={order.dateAdded} />,
        Location: <LocationCell order={order} locations={locations} />,
        Delivery: <DeliveryCell order={order} />,
        AssignedUnit: (
          <AssignedUnitCell order={order} trackingVia={trackingVia} locations={locations} isOrderList={true} />
        ),
        RentalStart: <RentalStartCell order={order} />,
        RentalEnd: <RentalEndCell order={order} />,
        Status: <OrderStatusCell order={order} />,
        Amount: <AmountCell order={order} locations={locations} />,
        ActionColumn: (
          <ActionCell
            order={order}
            locations={locations}
            reload={() => {
              setIsLoading(true);
              getOrders();
            }}
          />
        ),
      },
    };
  };

  const getOrders = useCallback(
    debounce(async () => {
      if (locations.length) {
        const today = Interval.fromDateTimes(
          DateTime.local({ zone: locations[0].timezone }).startOf('day'),
          DateTime.local({ zone: locations[0].timezone }).endOf('day'),
        );
        setIsLoading(true);
        try {
          const [startingToday, endingToday] = await Promise.all([
            searchOrders({
              documentType: OrderDocumentType.Order,
              startDateFrom: today.start,
              startDateTo: today.end,
              sortBy: request.sortBy,
              sortDirection: request.sortDirection,
              pageNumber: 1,
              pageSize: 10000,
              keyword,
            }),
            searchOrders({
              documentType: OrderDocumentType.Order,
              endDateFrom: today.start,
              endDateTo: today.end,
              sortBy: request.sortBy,
              sortDirection: request.sortDirection,
              pageNumber: 1,
              pageSize: 10000,
              keyword,
            }),
          ]);
          const orders = mergeOrder(startingToday.items, endingToday.items, request.sortBy, request.sortDirection);
          setTableData(orders.map(buildDataRow));
          setOrders(orders);
          setIsLoading(false);
        } catch (e: any) {
          toast('Error loading order', 'error');
          setIsLoading(false);
          setTableData([]);
        }
      } else {
        setIsLoading(false);
        setTableData([]);
      }
    }, 500),
    [locations, keyword, request],
  );

  return (
    <>
      {orders.length > 0 && (
        <DownloadButton
          downloadFunction={() => {
            exportToCSV(
              orders,
              locations,
              `Today_Orders_${DateTime.local({
                zone: locations.length ? locations[0].timezone : undefined,
              })
                .toFormat('D')
                .replaceAll('/', '-')}`,
              [
                'OrderNumber',
                'CustomerName',
                'RequestedTime',
                'Location',
                'Delivery',
                'RentalStart',
                'RentalEnd',
                'Amount',
                'Status',
                'Action',
              ],
              'serialNumber',
            );
          }}
        />
      )}
      <DataTable
        noDataText="No Records Found"
        wrapperClassNames="rounded-t-none"
        className="border-none"
        isLoading={isLoading}
        columnNames={columns}
        data={tableData}
        data-cy={'todays_orders_table'}
        sort={{
          field: request.sortBy || 'id',
          direction: request.sortDirection || 'desc',
          onSort: (field, direction) => {
            setRequest({ ...request, sortBy: field as OrderSearchRequest['sortBy'], sortDirection: direction });
          },
        }}
      />
    </>
  );
};

const TomorrowsOrders = ({ trackingVia, keyword }: { trackingVia: 'serialNumber' | 'unitNumber'; keyword: string }) => {
  const locations = useContext(LocationContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<DataTableRow[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const { searchOrders } = useAPI();

  const searchKey = 'dashboard_tomorrow_orders';
  const params = useRememberQueryParams(searchKey);
  const { request, setRequest } = useStickyFilters<OrderSearchRequest>(
    searchKey,
    {
      sortBy: params?.has('sortBy') ? (params.get('sortBy')! as OrderSearchRequest['sortBy']) : 'id',
      sortDirection: params?.has('sortDirection')
        ? (params.get('sortDirection')! as OrderSearchRequest['sortDirection'])
        : 'desc',
    },
    () => {
      getOrders();
    },
    [keyword],
  );

  const columns: DataTableColumn[] = [
    { key: 'RowNum', label: '#' },
    { key: 'OrderNumber', label: 'Order No' },
    { key: 'CustomerName', label: 'Customer' },
    { key: 'RequestedTime', label: 'Requested Time' },
    { key: 'Location', label: 'Location' },
    { key: 'Delivery', label: 'Delivery' },
    { key: 'AssignedUnit', label: 'Assigned Unit(s)' },
    { key: 'RentalStart', label: 'Rental Start', sortable: true, sortField: 'rentalStart' },
    { key: 'RentalEnd', label: 'Rental End', sortable: true, sortField: 'rentalEnd' },
    { key: 'Status', label: 'Status' },
    { key: 'Amount', label: 'Amount' },
    { key: 'ActionColumn', label: 'Actions' },
  ];

  const buildDataRow = (order: Order) => {
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium">
            {order.user.name}
          </Typography>
          <Typography type="text-sm" weight="medium">
            <Typography type="text-sm" weight="regular" className="inline text-[var(--grey-500)]">
              Inv. NO{' '}
            </Typography>
            {order.orderNumber}
          </Typography>
        </>
      ),
      fields: {
        RowNum: <RowNumCell order={order} />,
        OrderNumber: <OrderNumberCell order={order} />,
        CustomerName: <CustomerNameCell order={order} />,
        RequestedTime: <DateTimeCell date={order.dateAdded} />,
        Location: <LocationCell order={order} locations={locations} />,
        Delivery: <DeliveryCell order={order} />,
        AssignedUnit: (
          <AssignedUnitCell order={order} trackingVia={trackingVia} locations={locations} isOrderList={true} />
        ),
        RentalStart: <RentalStartCell order={order} />,
        RentalEnd: <RentalEndCell order={order} />,
        Status: <OrderStatusCell order={order} />,
        Amount: <AmountCell order={order} locations={locations} />,
        ActionColumn: (
          <ActionCell
            order={order}
            locations={locations}
            reload={() => {
              setIsLoading(true);
              getOrders();
            }}
          />
        ),
      },
    };
  };

  const getOrders = useCallback(
    debounce(async () => {
      if (locations.length) {
        const tomorrow = Interval.fromDateTimes(
          DateTime.local({ zone: locations[0].timezone }).plus({ days: 1 }).startOf('day'),
          DateTime.local({ zone: locations[0].timezone }).plus({ days: 1 }).endOf('day'),
        );
        setIsLoading(true);
        try {
          const [startingTomorrow, endingTomorrow] = await Promise.all([
            searchOrders({
              documentType: OrderDocumentType.Order,
              startDateFrom: tomorrow.start,
              startDateTo: tomorrow.end,
              sortBy: request.sortBy,
              sortDirection: request.sortDirection,
              pageNumber: 1,
              pageSize: 10000,
              keyword,
            }),
            searchOrders({
              documentType: OrderDocumentType.Order,
              endDateFrom: tomorrow.start,
              endDateTo: tomorrow.end,
              sortBy: request.sortBy,
              sortDirection: request.sortDirection,
              pageNumber: 1,
              pageSize: 10000,
              keyword,
            }),
          ]);
          const orders = mergeOrder(
            startingTomorrow.items,
            endingTomorrow.items,
            request.sortBy,
            request.sortDirection,
          );
          setTableData(orders.map(buildDataRow));
          setOrders(orders);
          setIsLoading(false);
        } catch (e: any) {
          toast('Error loading order', 'error');
          setIsLoading(false);
          setTableData([]);
        }
      } else {
        setIsLoading(false);
        setTableData([]);
      }
    }, 500),
    [request, keyword, locations],
  );

  return (
    <>
      {orders.length > 0 && (
        <DownloadButton
          downloadFunction={() => {
            exportToCSV(
              orders,
              locations,
              `Tomorrow_Orders_${DateTime.local({
                zone: locations.length ? locations[0].timezone : undefined,
              })
                .toFormat('D')
                .replaceAll('/', '-')}`,
              [
                'OrderNumber',
                'CustomerName',
                'RequestedTime',
                'Location',
                'Delivery',
                'RentalStart',
                'RentalEnd',
                'Amount',
                'Status',
                'Action',
              ],
              'unitNumber',
            );
          }}
        />
      )}
      <DataTable
        noDataText="No Records Found"
        wrapperClassNames="rounded-t-none"
        className="border-none"
        isLoading={isLoading}
        columnNames={columns}
        data={tableData}
        data-cy={'tomorrow_orders_table'}
        sort={{
          field: request.sortBy || 'id',
          direction: request.sortDirection || 'desc',
          onSort: (field, direction) => {
            setRequest({ ...request, sortBy: field as OrderSearchRequest['sortBy'], sortDirection: direction });
          },
        }}
      />
    </>
  );
};

const Latest5Orders = ({ trackingVia, keyword }: { trackingVia: 'serialNumber' | 'unitNumber'; keyword: string }) => {
  const locations = useContext(LocationContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<DataTableRow[]>([]);
  const { searchOrders } = useAPI();

  const columns: DataTableColumn[] = [
    { key: 'RowNum', label: '#' },
    { key: 'OrderNumber', label: 'Order No' },
    { key: 'CustomerName', label: 'Customer' },
    { key: 'RequestedTime', label: 'Requested Time' },
    { key: 'Location', label: 'Location' },
    { key: 'Delivery', label: 'Delivery' },
    { key: 'AssignedUnit', label: 'Assigned Unit(s)' },
    { key: 'RentalStart', label: 'Rental Start' },
    { key: 'RentalEnd', label: 'Rental End' },
    { key: 'Status', label: 'Status' },
    { key: 'Amount', label: 'Amount' },
    { key: 'ActionColumn', label: 'Actions' },
  ];

  const buildDataRow = (order: Order) => {
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium">
            {order.user.name}
          </Typography>
          <Typography type="text-sm" weight="medium">
            <Typography type="text-sm" weight="regular" className="inline text-[var(--grey-500)]">
              Inv. NO{' '}
            </Typography>
            {order.orderNumber}
          </Typography>
        </>
      ),
      fields: {
        RowNum: <RowNumCell order={order} />,
        OrderNumber: <OrderNumberCell order={order} />,
        CustomerName: <CustomerNameCell order={order} />,
        RequestedTime: <DateTimeCell date={order.dateAdded} />,
        Location: <LocationCell order={order} locations={locations} />,
        Delivery: <DeliveryCell order={order} />,
        AssignedUnit: (
          <AssignedUnitCell order={order} trackingVia={trackingVia} locations={locations} isOrderList={true} />
        ),
        RentalStart: <RentalStartCell order={order} />,
        RentalEnd: <RentalEndCell order={order} />,
        Status: <OrderStatusCell order={order} />,
        Amount: <AmountCell order={order} locations={locations} />,
        ActionColumn: (
          <ActionCell
            order={order}
            locations={locations}
            reload={() => {
              setIsLoading(true);
              getOrders();
            }}
          />
        ),
      },
    };
  };

  useEffect(() => {
    getOrders();
  }, [locations, keyword]);

  const getOrders = useCallback(() => {
    if (locations.length) {
      searchOrders({
        documentType: OrderDocumentType.Order,
        keyword: keyword,
        sortBy: 'id',
        sortDirection: 'desc',
        pageNumber: 1,
        pageSize: 5,
      })
        .then(({ items }) => {
          setTableData(items.map(buildDataRow));
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
          setTableData([]);
        });
    } else {
      setIsLoading(false);
      setTableData([]);
    }
  }, [locations, keyword]);

  return (
    <DataTable
      noDataText="No Records Found"
      wrapperClassNames="rounded-t-none"
      className="border-none"
      isLoading={isLoading}
      columnNames={columns}
      data={tableData}
      data-cy={'latest_5_orders_table'}
    />
  );
};

const PastDueOrders = ({ trackingVia, keyword }: { trackingVia: 'serialNumber' | 'unitNumber'; keyword: string }) => {
  const locations = useContext(LocationContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<DataTableRow[]>([]);
  const { searchOrders } = useAPI();

  const searchKey = 'dashboard_past_due_orders';
  const params = useRememberQueryParams(searchKey);
  const { request, setRequest } = useStickyFilters<OrderSearchRequest>(
    searchKey,
    {
      sortBy: params?.has('sortBy') ? (params.get('sortBy')! as OrderSearchRequest['sortBy']) : 'id',
      sortDirection: params?.has('sortDirection')
        ? (params.get('sortDirection')! as OrderSearchRequest['sortDirection'])
        : 'desc',
    },
    () => {
      if (locations.length) {
        getOrders();
      } else {
        setIsLoading(false);
        setTableData([]);
      }
    },
    [keyword],
  );

  const columns: DataTableColumn[] = [
    { key: 'RowNum', label: '#' },
    { key: 'OrderNumber', label: 'Order No' },
    { key: 'ProductName', label: 'Product(s)' },
    { key: 'AssignedUnit', label: 'Assigned Unit(s)' },
    { key: 'CustomerName', label: 'Customer' },
    { key: 'RentalStart', label: 'Rental Start', sortable: true, sortField: 'rentalStart' },
    { key: 'RentalEnd', label: 'Rental End', sortable: true, sortField: 'rentalEnd' },
    { key: 'PastDue', label: 'Past Due' },
  ];

  const buildDataRow = (order: Order) => {
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium">
            {order.user.name}
          </Typography>
          <Typography type="text-sm" weight="medium">
            <Typography type="text-sm" weight="regular" className="inline text-[var(--grey-500)]">
              Inv. NO{' '}
            </Typography>
            {order.orderNumber}
          </Typography>
        </>
      ),
      fields: {
        RowNum: <RowNumCell order={order} />,
        OrderNumber: <OrderNumberCell order={order} />,
        ProductName: <ProductNameCell order={order} />,
        AssignedUnit: (
          <AssignedUnitCell order={order} trackingVia={trackingVia} locations={locations} isOrderList={true} />
        ),
        CustomerName: <CustomerNameCell order={order} />,
        RentalStart: <RentalStartCell order={order} />,
        RentalEnd: <RentalEndCell order={order} />,
        PastDue: <PastDueCell order={order} />,
      },
    };
  };

  const getOrders = useCallback(
    debounce(() => {
      const now = DateTime.local({ zone: locations[0].timezone });
      setIsLoading(true);
      searchOrders({
        documentType: OrderDocumentType.Order,
        to: now,
        status: OrderStatus.Approved,
        returnStatus: OrderReturnStatus.OnRent,
        sortBy: request.sortBy,
        sortDirection: request.sortDirection,
        pageNumber: 1,
        pageSize: 10000,
        keyword,
      } as OrderSearchRequest)
        .then(({ items }) => {
          const pastDueOrders = items
            .filter((o) =>
              o.products.some(
                (op) =>
                  op.rentalEndDate &&
                  op.rentalEndDate.diff(now).milliseconds < 0 &&
                  op.status === OrderReturnStatus.OnRent,
              ),
            )
            .flatMap((o) =>
              o.products
                .filter(
                  (op) =>
                    op.rentalEndDate &&
                    op.rentalEndDate.diff(now).milliseconds < 0 &&
                    op.status === OrderReturnStatus.OnRent,
                )
                .map((op) => {
                  return { ...o, products: [op] };
                }),
            );

          setTableData(pastDueOrders.map(buildDataRow));
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
          setTableData([]);
        });
    }, 500),
    [request, keyword],
  );

  return (
    <DataTable
      noDataText="No Records Found"
      wrapperClassNames="rounded-t-none"
      className="border-none"
      isLoading={isLoading}
      columnNames={columns}
      data={tableData}
      data-cy={'past_due_orders_table'}
      sort={{
        field: request.sortBy || 'id',
        direction: request.sortDirection || 'desc',
        onSort: (field, direction) => {
          setRequest({ ...request, sortBy: field as OrderSearchRequest['sortBy'], sortDirection: direction });
        },
      }}
    />
  );
};

const CancelledOrders = ({ trackingVia, keyword }: { trackingVia: 'serialNumber' | 'unitNumber'; keyword: string }) => {
  const locations = useContext(LocationContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<DataTableRow[]>([]);
  const { searchOrders } = useAPI();

  const searchKey = 'dashboard_cancelled_orders';
  const params = useRememberQueryParams(searchKey);
  const { request, setRequest } = useStickyFilters<OrderSearchRequest>(
    searchKey,
    {
      sortBy: params?.has('sortBy') ? (params.get('sortBy')! as OrderSearchRequest['sortBy']) : 'id',
      sortDirection: params?.has('sortDirection')
        ? (params.get('sortDirection')! as OrderSearchRequest['sortDirection'])
        : 'desc',
    },
    () => {
      if (locations.length) {
        search();
      } else {
        setIsLoading(false);
        setTableData([]);
      }
    },
    [keyword],
  );

  const columns: DataTableColumn[] = [
    { key: 'RowNum', label: '#' },
    { key: 'OrderNumber', label: 'Order No' },
    { key: 'ProductName', label: 'Product(s)' },
    { key: 'AssignedUnit', label: 'Assigned Unit(s)' },
    { key: 'RentalStart', label: 'Rental Start', sortable: true, sortField: 'rentalStart' },
    { key: 'RentalEnd', label: 'Rental End', sortable: true, sortField: 'rentalEnd' },
    { key: 'RequestedTime', label: 'Requested Time' },
    { key: 'CancelledTime', label: 'Canceled at' },
    { key: 'CancelledReason', label: 'Cancellation Reason' },
  ];

  const buildDataRow = (order: Order) => {
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium">
            {order.user.name}
          </Typography>
          <Typography type="text-sm" weight="medium">
            <Typography type="text-sm" weight="regular" className="inline text-[var(--grey-500)]">
              Inv. NO{' '}
            </Typography>
            {order.orderNumber}
          </Typography>
        </>
      ),
      fields: {
        RowNum: <RowNumCell order={order} />,
        OrderNumber: <OrderNumberCell order={order} />,
        ProductName: <ProductNameCell order={order} />,
        AssignedUnit: (
          <AssignedUnitCell order={order} trackingVia={trackingVia} locations={locations} isOrderList={true} />
        ),
        RentalStart: <RentalStartCell order={order} />,
        RentalEnd: <RentalEndCell order={order} />,
        RequestedTime: <DateTimeCell date={order.dateAdded} />,
        CancelledTime: order.cancellationDate && <DateTimeCell date={order.cancellationDate} />,
        CancelledReason: <CancelledReasonCell order={order} />,
      },
    };
  };

  const search = useCallback(
    debounce(() => {
      setIsLoading(true);
      searchOrders({
        documentType: OrderDocumentType.Order,
        to: DateTime.local({ zone: locations[0].timezone }),
        status: OrderStatus.Cancelled,
        sortBy: request.sortBy,
        sortDirection: request.sortDirection,
        pageNumber: 1,
        pageSize: 10000,
        keyword,
      } as OrderSearchRequest)
        .then(({ items }) => {
          setTableData(items.map(buildDataRow));
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
          setTableData([]);
        });
    }, 500),
    [request, keyword],
  );

  return (
    <DataTable
      noDataText="No Records Found"
      wrapperClassNames="rounded-t-none"
      className="border-none"
      isLoading={isLoading}
      columnNames={columns}
      data={tableData}
      data-cy={'cancelled_orders_table'}
      sort={{
        field: request.sortBy || 'id',
        direction: request.sortDirection || 'desc',
        onSort: (field, direction) => {
          setRequest({ ...request, sortBy: field as OrderSearchRequest['sortBy'], sortDirection: direction });
        },
      }}
    />
  );
};

function sortOrderByIdAsc(o1: Order, o2: Order) {
  return o1.id - o2.id;
}

function sortOrderByIdDesc(o1: Order, o2: Order) {
  return sortOrderByIdAsc(o2, o1);
}

function maxRentalEndFn(o: Order): DateTime | undefined {
  const endDates = o.products
    .filter((op) => op.type === OrderType.Rental)
    .flatMap((op) => {
      const end = op.actualRentalEndDate || op.rentalEndDate;
      if (end) {
        return [end];
      } else {
        return [];
      }
    });
  if (endDates.length > 0) {
    return DateTime.max(...endDates);
  } else {
    return undefined;
  }
}

function sortOrderByRentalEndAsc(o1: Order, o2: Order) {
  const o1End = maxRentalEndFn(o1);
  const o2End = maxRentalEndFn(o2);
  if (!o1End) {
    return 1;
  } else if (!o2End) {
    return -1;
  } else {
    return o1End.diff(o2End).milliseconds;
  }
}

function sortOrderByRentalEndDesc(o1: Order, o2: Order) {
  return sortOrderByRentalEndAsc(o2, o1);
}

function maxRentalStart(o: Order): DateTime {
  return DateTime.max(
    ...o.products
      .filter((op) => op.type === OrderType.Rental)
      .map((op) => op.actualRentalStartDate || op.rentalStartDate),
  );
}

function sortOrderByRentalStartAsc(o1: Order, o2: Order) {
  const o1Start = maxRentalStart(o1);
  const o2Start = maxRentalStart(o2);
  return o1Start.diff(o2Start).milliseconds;
}

function sortOrderByRentalStartDesc(o1: Order, o2: Order) {
  return sortOrderByRentalStartAsc(o2, o1);
}

export function mergeOrder(
  s1: Order[],
  s2: Order[],
  sortBy: 'id' | 'rentalStart' | 'rentalEnd' = 'id',
  sortDirection: 'asc' | 'desc' = 'desc',
): Order[] {
  const dedupedS2 = s2.filter((o2) => !s1.some((o1) => o1.id === o2.id));
  let sortFunction;
  switch (sortBy) {
    case 'id': {
      if (sortDirection === 'asc') {
        sortFunction = sortOrderByIdAsc;
      } else {
        sortFunction = sortOrderByIdDesc;
      }
      break;
    }
    case 'rentalEnd': {
      if (sortDirection === 'asc') {
        sortFunction = sortOrderByRentalEndAsc;
      } else {
        sortFunction = sortOrderByRentalEndDesc;
      }
      break;
    }
    case 'rentalStart': {
      if (sortDirection === 'asc') {
        sortFunction = sortOrderByRentalStartAsc;
      } else {
        sortFunction = sortOrderByRentalStartDesc;
      }
      break;
    }
  }
  let idx1 = 0;
  let idx2 = 0;
  const merged: Order[] = [];
  while (idx1 < s1.length && idx2 < dedupedS2.length) {
    const o1 = s1[idx1];
    const o2 = dedupedS2[idx2];
    const comp = sortFunction(o1, o2);
    if (comp >= 0) {
      if (!merged.some((o) => o.id === o2.id)) {
        merged.push(o2);
      }
      idx2++;
    } else {
      if (!merged.some((o) => o.id === o1.id)) {
        merged.push(o1);
      }
      idx1++;
    }
  }
  if (idx1 >= s1.length) {
    merged.push(...dedupedS2.slice(idx2));
  } else {
    merged.push(...s1.slice(idx1));
  }
  return merged;
}
